import { getLocalISODate as getAdminLocalISODate } from "../admin/utils";

export * from "../admin/utils";

export const getLocalISODate = getAdminLocalISODate;

const normalizeBookingValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isActiveLikeStatus = (status) => {
  const normalizedStatus = normalizeBookingValue(status);
  return !normalizedStatus || normalizedStatus === "active";
};

const isPermanentBooking = (booking) => {
  const dateValue = normalizeBookingValue(booking?.date);
  const bookingTypeValue = normalizeBookingValue(
    booking?.bookingType || booking?.booking_type || booking?.type,
  );

  return (
    isActiveLikeStatus(booking?.status) &&
    (dateValue === "permanent" || bookingTypeValue === "permanent")
  );
};

const isRecurringBooking = (booking) => {
  const bookingTypeValue = normalizeBookingValue(
    booking?.bookingType || booking?.booking_type || booking?.type,
  );

  return isActiveLikeStatus(booking?.status) && bookingTypeValue === "recurring";
};

export const getSeatStatus = (
  seat,
  allBookings,
  selectedDate,
  currentUser,
  requirement,
  availableSeats = null,
  blockedSeatIds = [],
  reservedSeatIds = [],
  permanentSeatIds = [],
  recurringSeatIds = [],
) => {
  const currentUserId = currentUser?.id;
  const currentUserName = currentUser?.name;
  
  const isBlocked = 
    normalizeBookingValue(seat?.restrictionType) === "block" ||
    normalizeBookingValue(seat?.booking_details?.restriction_type) === "block" ||
    normalizeBookingValue(seat?.booking_details?.type) === "block" ||
    normalizeBookingValue(seat?.bookingType) === "block" ||
    blockedSeatIds.includes(seat.id);

  if (isBlocked) {
    return { status: "blocked", booking: null };
  }

  // Server-authoritative explicit availability check takes precedence.
  // If the server says it's available, it overrides local permanent/recurring records
  // (e.g., if a permanent seat was cancelled for this specific date).
  if (Array.isArray(availableSeats) && availableSeats.includes(seat.id)) {
    const typeCompatible = seat.type === requirement;
    return { status: typeCompatible ? "available" : "incompatible", booking: null };
  }

  // Check permanent assignments first (always takes priority)
  const permanentBooking = allBookings.find(
    (b) => b.seatId === seat.id && isPermanentBooking(b)
  );
  
  const permanentUserId = seat.permanentUserId || permanentBooking?.bookedFor;

  if (permanentUserId) {
    const isMine = 
      (currentUserId && permanentUserId === currentUserId) || 
      (currentUserName && permanentUserId === currentUserName);

    return {
      status: isMine ? "my-permanent" : "permanent",
      booking: permanentBooking || { bookedFor: permanentUserId },
    };
  }

  if (Array.isArray(permanentSeatIds) && permanentSeatIds.includes(seat.id)) {
    return { status: "permanent", booking: null };
  }

  const recurringBooking = allBookings.find(
    (b) => b.seatId === seat.id && isRecurringBooking(b) && b.date === selectedDate,
  );
  if (recurringBooking) {
    return { status: "recurring", booking: recurringBooking };
  }

  if (Array.isArray(recurringSeatIds) && recurringSeatIds.includes(seat.id)) {
    return { status: "recurring", booking: null };
  }

  // Check if the user already has a booking on this seat for this date
  const dateBooking = allBookings.find(
    (b) => b.status === "active" && b.seatId === seat.id && b.date === selectedDate
  );
  if (dateBooking) {
    const isMine = 
      (currentUserId && dateBooking.bookedFor === currentUserId) || 
      (currentUserName && dateBooking.bookedFor === currentUserName);

    return {
      status: isMine ? "mine" : "booked",
      booking: dateBooking,
    };
  }

  if (Array.isArray(reservedSeatIds) && reservedSeatIds.includes(seat.id)) {
    return { status: "reserved", booking: null };
  }

  // Server-authoritative availability check:
  // If availableSeats was fetched from the search API, a seat NOT in the list
  // is occupied by someone else's booking (admin or other user).
  if (Array.isArray(availableSeats) && !availableSeats.includes(seat.id)) {
    return { status: "booked", booking: null };
  }

  const typeCompatible = seat.type === requirement;

  return { status: typeCompatible ? "available" : "incompatible", booking: null };
};

export const getSectionAvailability = (
  section,
  allBookings,
  selectedDate,
  requirement,
  availableSeats = null,
  blockedSeatIds = [],
  reservedSeatIds = [],
  permanentSeatIds = [],
  recurringSeatIds = [],
) => {
  const matching = section.seats.filter((s) => s.type === requirement);

  const available = matching.filter((seat) => {
    // Use server-authoritative availability when available to override permanent blocks
    if (Array.isArray(availableSeats)) {
      return availableSeats.includes(seat.id);
    }

    if (seat.permanentUserId) return false;
    const permanent = allBookings.find(
      (b) => b.seatId === seat.id && isPermanentBooking(b)
    );
    if (permanent) return false;

    if (Array.isArray(reservedSeatIds) && reservedSeatIds.includes(seat.id)) {
      return false;
    }

    if (Array.isArray(permanentSeatIds) && permanentSeatIds.includes(seat.id)) {
      return false;
    }

    const isBlocked = 
      normalizeBookingValue(seat?.restrictionType) === "block" ||
      normalizeBookingValue(seat?.booking_details?.restriction_type) === "block" ||
      normalizeBookingValue(seat?.booking_details?.type) === "block" ||
      normalizeBookingValue(seat?.bookingType) === "block" ||
      blockedSeatIds.includes(seat.id);

    if (isBlocked) {
      return false;
    }

    const recurring = allBookings.find(
      (b) => b.seatId === seat.id && b.date === selectedDate && isRecurringBooking(b),
    );
    if (recurring) return false;

    if (Array.isArray(recurringSeatIds) && recurringSeatIds.includes(seat.id)) {
      return false;
    }

    const dated = allBookings.find(
      (b) => b.status === "active" && b.seatId === seat.id && b.date === selectedDate
    );
    return !dated;
  });

  return { total: matching.length, available: available.length };
};

export const buildUserName = (userInfo) => {
  const inner = userInfo?.userInfo || userInfo;
  const first = inner?.first_name || "";
  const last = inner?.last_name || "";
  const full = `${first} ${last}`.trim();
  return full || inner?.email || "You";
};

export const getQuickDates = () => {
  const dates = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    dates.push(getLocalISODate(d));
  }
  return dates;
};

/**
 * Converts an ISO date string (yyyy-mm-dd) to dd-mm-yyyy for UI display.
 * Returns the original value unchanged if it is not a valid date string (e.g. "permanent").
 */
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr;
  return `${day}-${month}-${year}`;
};

export const formatLongDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

export const formatUpperDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
    .format(date)
    .toUpperCase();
};
